import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { measureTextWidth } from 'utils/helpers';

export type NumberProps = {
  onChange?: (value: number | undefined) => void;
  debounce?: number;
  value?: any;
  placeholder?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  autoFocus?: boolean;
  autoSelect?: boolean;
  size?: 'sm' | 'md';
  hideControls?: boolean;
  prefix?: string;
  suffix?: string;
  invalid?: boolean;
  step?: number;
};

export default function NumberInput({
  onChange,
  debounce,
  value: initialValue,
  placeholder,
  disabled,
  min,
  max,
  autoFocus,
  autoSelect,
  size = 'md',
  hideControls,
  prefix,
  suffix,
  invalid,
  step = 1
}: NumberProps) {
  const [value, setValue] = useState<string>((initialValue ?? '').toString());
  const [prefixWidth, setPrefixWidth] = useState(0);
  const [valueWidth, setValueWidth] = useState(0);
  const isUserInteractionRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const ref = useRef<HTMLInputElement>(null);
  onChangeRef.current = onChange;

  useEffect(() => {
    isUserInteractionRef.current = false;
    setValue((initialValue ?? '').toString());
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (autoSelect && ref.current) {
      ref.current.select();
    }
  }, [autoSelect]);

  const emitChange = (nextValue: number | undefined) => {
    if (onChangeRef.current) {
      onChangeRef.current(nextValue);
    }
  };

  const adjustValue = () => {
    if (value !== '' && !Number.isNaN(Number(value))) {
      let newValue = Number(value);
      if (min !== undefined) {
        newValue = Math.max(min, newValue);
      }
      if (max !== undefined) {
        newValue = Math.min(max, newValue);
      }
      if (newValue !== Number(value)) {
        setValue(newValue.toString());
        emitChange(newValue);
      }
    } else if (isUserInteractionRef.current) {
      emitChange(undefined);
    }
  };

  useEffect(() => {
    if (prefix) {
      setPrefixWidth(measureTextWidth(prefix, 14));
    }
  }, [prefix]);

  useEffect(() => {
    setValueWidth(measureTextWidth(value, 14));
    if (!isUserInteractionRef.current) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      emitChange(value !== '' ? Number(value) : undefined);
    }, debounce);
    return () => {
      clearTimeout(timeout);
    };
  }, [value, debounce]);

  const classes = clsx(
    'block w-full relative rounded-md border-gray-300 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 text-sm',
    disabled && 'opacity-[70%] cursor-not-allowed',
    size === 'sm' && 'px-2 py-1',
    size === 'md' && 'px-3 py-2',
    hideControls &&
      '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
    invalid && 'ring-2 ring-red-500'
  );

  return (
    <div className='relative w-full'>
      <input
        ref={ref}
        type='number'
        className={classes}
        placeholder={placeholder}
        onChange={(e) => {
          isUserInteractionRef.current = true;
          setValue(e.target.value);
        }}
        onBlur={adjustValue}
        value={value}
        disabled={disabled}
        min={min}
        max={max}
        style={
          prefix && value !== ''
            ? { paddingLeft: (size === 'sm' ? 8 : 12) + prefixWidth }
            : undefined
        }
        step={step}
      />
      <div className='pointer-events-none absolute inset-0 overflow-hidden'>
        {value !== '' && prefix && (
          <span
            className='pointer-events-none absolute inset-y-0 flex items-center whitespace-pre text-sm'
            style={{ left: size === 'sm' ? 8 : 12 }}
          >
            {prefix}
          </span>
        )}
        {value !== '' && suffix && (
          <span
            className='pointer-events-none absolute inset-y-0 flex items-center whitespace-pre text-sm'
            style={{
              left: (size === 'sm' ? 8 : 12) + valueWidth + prefixWidth
            }}
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}
